import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Home, Zap, ChevronRight, Clock, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGOS = {
  orange: "/images/logos/orange.svg"
};

const ThanksPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [energyInterestAdded, setEnergyInterestAdded] = useState(false);
  const [showEnergyForm, setShowEnergyForm] = useState(false);
  const [currentEnergyProvider, setCurrentEnergyProvider] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    // Get form data from location state (passed from HomePage) or session storage
    if (location.state?.formData) {
      setFormData(location.state.formData);
      // Also store in sessionStorage for page refresh
      sessionStorage.setItem('leadFormData', JSON.stringify(location.state.formData));
    } else {
      const storedData = sessionStorage.getItem('leadFormData');
      if (storedData) {
        setFormData(JSON.parse(storedData));
      }
    }
  }, [location.state]);

  // Google Analytics conversie tracking
  useEffect(() => {
    const trackConversion = async () => {
      try {
        const response = await axios.get(`${API}/seo-settings`);
        const { google_analytics_id, conversion_event_name, conversion_value } = response.data;
        
        if (google_analytics_id && window.gtag) {
          const eventName = conversion_event_name || 'generate_lead';
          const eventParams = {
            event_category: 'Lead',
            event_label: 'Telecom Lead Formulier',
          };
          
          if (conversion_value && parseFloat(conversion_value) > 0) {
            eventParams.value = parseFloat(conversion_value);
            eventParams.currency = 'EUR';
          }
          
          window.gtag('event', eventName, eventParams);
        }
      } catch (error) {
        console.log('Kon conversie tracking niet laden');
      }
    };
    
    trackConversion();
  }, []);

  const energyProviders = [
    'Engie', 'Luminus', 'Elegant', 'Mega', 'Bolt', 
    'Octa+', 'Eneco', 'TotalEnergies', 'Essent', 'Andere'
  ];

  const handleEnergySubmit = async () => {
    if (!currentEnergyProvider) {
      toast.error('Selecteer eerst uw huidige energieleverancier');
      return;
    }

    if (!formData.email) {
      toast.error('Er ging iets mis. Vernieuw de pagina en probeer opnieuw.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.patch(`${API}/leads/update-energy`, {
        email: formData.email,
        energy_interest: true,
        current_energy_provider: currentEnergyProvider
      });
      
      setEnergyInterestAdded(true);
      setShowEnergyForm(false);
      toast.success('Energie-interesse is toegevoegd aan uw aanvraag!');
    } catch (error) {
      console.error('Energy update error:', error);
      toast.error('Er ging iets mis. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="text-xl font-bold text-blue-600">Telecomprijsvergelijker<span className="text-orange-500">.be</span></div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
                <img src={LOGOS.orange} alt="Orange" className="h-5" />
                <span className="text-xs font-medium text-orange-700">Officieel Partner</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Success Card */}
        <Card className="mb-8 border-0 shadow-lg">
          <CardContent className="p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="success-title">
              Bedankt voor uw aanvraag!
            </h1>
            <p className="text-lg text-slate-600 mb-2">
              We hebben uw prijsvergelijking aanvraag ontvangen.
            </p>
            <p className="text-xl font-medium text-blue-600">
              Binnen 24 uur nemen wij contact met u op.
            </p>
          </CardContent>
        </Card>

        {/* What's Next */}
        <Card className="mb-8 border-0 shadow-md">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Wat gebeurt er nu?
            </h2>
            
            <div className="space-y-4">
              {[
                { num: 1, title: "Analyse van uw situatie", desc: "We bekijken uw huidige abonnement en berekenen uw besparing" },
                { num: 2, title: "Persoonlijk voorstel", desc: "U ontvangt een op maat gemaakt voorstel met de beste prijs" },
                { num: 3, title: "Gratis overstapservice", desc: "Wij komen langs voor uitleg en regelen al het papierwerk" }
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">{num}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{title}</h3>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Energy Interest Section - More prominent */}
        {!energyInterestAdded ? (
          <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
            <CardContent className="p-6 md:p-8 relative">
              {!showEnergyForm ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-4">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-semibold">Extra besparing mogelijk!</span>
                  </div>
                  
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                      <Zap className="w-10 h-10 text-yellow-300" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-3">
                    Wilt u ook besparen op energie?
                  </h3>
                  <p className="text-green-100 mb-6 max-w-md mx-auto">
                    Als officieel <strong className="text-white">Eneco partner</strong> kunnen we ook uw 
                    energiekosten vergelijken en u helpen besparen!
                  </p>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Button 
                      onClick={() => setShowEnergyForm(true)}
                      className="bg-white text-green-700 hover:bg-green-50 font-semibold px-6 py-3 h-auto"
                      data-testid="add-energy-button"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Ja, voeg energie toe!
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setEnergyInterestAdded(true)}
                      className="border-white/50 text-white hover:bg-white/10 hover:text-white"
                      data-testid="skip-energy-button"
                    >
                      Nee, alleen telecom
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-xl font-bold mb-4">
                    <Zap className="w-6 h-6 inline mr-2" />
                    Wie is uw huidige energieleverancier?
                  </h3>
                  <p className="text-green-100 mb-6">
                    Zo kunnen we berekenen hoeveel u kunt besparen met Eneco.
                  </p>
                  
                  <div className="max-w-xs mx-auto mb-6">
                    <Select value={currentEnergyProvider} onValueChange={setCurrentEnergyProvider}>
                      <SelectTrigger className="bg-white text-slate-900" data-testid="energy-provider-select">
                        <SelectValue placeholder="Selecteer leverancier..." />
                      </SelectTrigger>
                      <SelectContent>
                        {energyProviders.map(provider => (
                          <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Button 
                      onClick={handleEnergySubmit}
                      disabled={!currentEnergyProvider || isSubmitting}
                      className="bg-white text-green-700 hover:bg-green-50 font-semibold"
                      data-testid="submit-energy-button"
                    >
                      {isSubmitting ? 'Bezig...' : (
                        <>
                          <CheckCircle2 className="w-5 h-5 mr-2" />
                          Toevoegen aan aanvraag
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowEnergyForm(false)}
                      className="border-white/50 text-white hover:bg-white/10 hover:text-white"
                    >
                      Terug
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Energy Interest Confirmation */
          energyInterestAdded && currentEnergyProvider && (
            <Card className="mb-8 border-2 border-green-300 bg-green-50">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-green-800 mb-2">
                  Energie-vergelijking toegevoegd!
                </h3>
                <p className="text-green-700">
                  We vergelijken ook <strong>{currentEnergyProvider}</strong> met Eneco en 
                  nemen hierover contact met u op.
                </p>
              </CardContent>
            </Card>
          )
        )}

        {/* Back to Home */}
        <div className="flex justify-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="home-button"
          >
            <Home className="w-4 h-4" />
            Terug naar home
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-xl font-bold text-white mb-4">
                Telecomprijsvergelijker<span className="text-orange-500">.be</span>
              </div>
              <p className="text-sm mb-4">
                Wij helpen u besparen op TV, internet en mobiel door Telenet, Proximus en Base te vergelijken met Orange's aanbod.
              </p>
              <div className="flex items-center gap-3">
                <img src={LOGOS.orange} alt="Orange Partner" className="h-8 opacity-80" />
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/privacybeleid-en-algemene-voorwaarden" className="hover:text-white transition-colors">Privacybeleid en algemene voorwaarden</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookiebeleid</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <p className="text-sm">Officieel Orange & Eneco partner via distributiekanaal SmartSN</p>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Telecomprijsvergelijker.be - Alle rechten voorbehouden</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ThanksPage;
